import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { fetchAddressData, fetchAddressTransactions } from '../services/apiService';
import { QRCodeSVG } from 'qrcode.react';
import useTitle from '../hooks/useTitle';

import {
  FaWallet,
  FaArrowDown,
  FaArrowUp,
  FaBalanceScale,
  FaClock,
  FaLink,
} from 'react-icons/fa';

const AddressPage = () => {
  const { address } = useParams();
  useTitle("MENELIK - Address");

  const [addressData, setAddressData] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);

  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loadingTransactions, setLoadingTransactions] = useState(false);

  useEffect(() => {
    setAddressData(null);
    setTransactions([]);
    setPage(1);
    setHasMore(true);
  }, [address]);

  useEffect(() => {
    const loadAddressData = async () => {
      setLoading(true);
      try {
        const data = await fetchAddressData(address);
        setAddressData(data);
      } catch (err) {
        console.error('Error fetching address data:', err);
        setAddressData(null);
      } finally {
        setLoading(false);
      }
    };

    loadAddressData();
  }, [address]);

  useEffect(() => {
    const loadTransactions = async () => {
      if (!addressData) return;
      if (!hasMore) return;
      setLoadingTransactions(true);

      try {
        const data = await fetchAddressTransactions(address, page);
        console.log('Página:', page, 'Transacciones devueltas:', data.length);

        if (data.length === 0) {
          // No hay más
          setHasMore(false);
        } else {
          // Deduplicamos
          setTransactions((prev) => {
            const existingTxids = new Set(prev.map(tx => tx.tx_hash));
            const newTxs = data.filter(tx => !existingTxids.has(tx.tx_hash));
            return [...prev, ...newTxs];
          });
        }
      } catch (error) {
        console.error('Error fetching transactions:', error);
        setHasMore(false);
      } finally {
        setLoadingTransactions(false);
      }
    };

    loadTransactions();
  }, [addressData, page, hasMore, address]);

  const handleLoadMore = () => {
    setPage(prev => prev + 1);
  };

  const formatDirection = (dir) => {
    if (!dir) return '';
    return dir.toLowerCase() === 'incoming' ? 'Received' : 'Sent';
  };

  if (loading) {
    return <p className="text-center text-lg text-gray-500 my-8">Loading address data...</p>;
  }

  if (!addressData) {
    return (
      <p className="text-center text-lg text-gray-500 my-8">
        No data found for address: <span className="font-mono">{address}</span>
      </p>
    );
  }

  // Desestructurar datos
  const {
    address: addr,
    total_received,
    total_sent,
    balance,
    last_updated,
  } = addressData;

  return (
    <div className="container mx-auto p-4">
      <h1 className="text-3xl font-bold mb-6 flex items-center text-gray-800">
        <FaWallet className="mr-2 text-blue-600" />
        Address Details
      </h1>

      <div className="bg-white shadow-lg rounded-lg p-6 border border-gray-200">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <div className="mb-4">
              <h2 className="text-xl font-semibold flex items-center text-gray-800 mb-2">
                <FaWallet className="mr-2 text-blue-500" />
                Address
              </h2>
              <p className="break-all font-mono text-gray-700 text-sm sm:text-base">
                {addr}
               
              {/* si existe display_name */}
              {addressData.display_name && (
                addressData.url ? (
                  <a
                    href={addressData.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="ml-2 px-2 py-1 text-xs font-semibold text-white bg-blue-500 rounded-full shadow inline-flex items-center hover:bg-blue-600 transition"
                  >
                    {addressData.display_name}
                    <FaLink className="ml-1" />
                  </a>
                ) : (
                  <span className="ml-2 px-2 py-1 text-xs font-semibold text-white bg-blue-500 rounded-full shadow inline-flex">
                    {addressData.display_name}
                  </span>
                )
              )}
              </p>

            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="bg-gray-50 p-4 rounded shadow-sm border border-gray-100">
                <h3 className="flex items-center text-gray-800 font-semibold mb-1">
                  <FaArrowDown className="mr-2 text-green-600" />
                  Total Received
                </h3>
                <p className="text-gray-700 break-all">{total_received}</p>
              </div>
              <div className="bg-gray-50 p-4 rounded shadow-sm border border-gray-100">
                <h3 className="flex items-center text-gray-800 font-semibold mb-1">
                  <FaArrowUp className="mr-2 text-red-600" />
                  Total Sent
                </h3>
                <p className="text-gray-700 break-all">{total_sent}</p>
              </div>
              <div className="bg-gray-50 p-4 rounded shadow-sm border border-gray-100">
                <h3 className="flex items-center text-gray-800 font-semibold mb-1">
                  <FaBalanceScale className="mr-2 text-purple-600" />
                  Balance
                </h3>
                <p className="text-gray-700 break-all">{balance}</p>
              </div>
              <div className="bg-gray-50 p-4 rounded shadow-sm border border-gray-100">
                <h3 className="flex items-center text-gray-800 font-semibold mb-1">
                  <FaClock className="mr-2 text-gray-600" />
                  Last Updated
                </h3>
                <p className="text-gray-700">
                  {new Date(last_updated).toLocaleString()}
                </p>
              </div>
            </div>
          </div>
          <div className="flex flex-col items-center justify-center">
            <div className="bg-gray-50 p-4 rounded shadow-sm border border-gray-100">
              <QRCodeSVG
                value={addr}
                size={180}
                bgColor="#ffffff"
                fgColor="#000000"
                level="L"
                includeMargin
              />
            </div>
            <p className="mt-2 text-gray-600 text-sm flex items-center">
              Scan your address <FaWallet className="ml-1 text-blue-500" />
            </p>
          </div>
        </div>
      </div>

      <div className="mt-8">
        <h2 className="text-2xl font-bold mb-4">Transactions</h2>
        {transactions.length === 0 ? (
          <p className="text-gray-600">No transactions found.</p>
        ) : (
          <table className="w-full table-auto border-collapse border border-gray-200">
            <thead>
              <tr className="bg-gray-100">
                <th className="border border-gray-200 px-4 py-2">Tx Hash</th>
                <th className="border border-gray-200 px-4 py-2">Amount</th>
                <th className="border border-gray-200 px-4 py-2">Balance</th>
                <th className="border border-gray-200 px-4 py-2">Direction</th>
                <th className="border border-gray-200 px-4 py-2">Timestamp</th>
              </tr>
            </thead>
            <tbody>
              {transactions.map((tx, i) => {
                const directionLabel = formatDirection(tx.direction);
                const isReceived = directionLabel === 'Received';
                return (
                  <tr
                    key={`${tx.tx_hash}-${i}`}
                    className="text-center"
                    style={{ backgroundColor: isReceived ? '#e6ffed' : '#ffe6e6' }}
                  >
                    <td className="border border-gray-200 px-4 py-2 break-all">
                      <Link
                        to={`/txid/${tx.tx_hash}`}
                        className="text-blue-500 hover:underline"
                      >
                        {tx.tx_hash}
                      </Link>
                    </td>
                    <td className="border border-gray-200 px-4 py-2">
                      {tx.amount}
                    </td>
                    <td className="border border-gray-200 px-4 py-2">
                      {tx.balance}
                    </td>
                    <td
                      className="border border-gray-200 px-4 py-2"
                      style={{ color: isReceived ? 'green' : 'red' }}
                    >
                      {directionLabel}
                    </td>
                    <td className="border border-gray-200 px-4 py-2">
                      {new Date(tx.timestamp).toLocaleString()}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
        {hasMore && (
          <button
            onClick={handleLoadMore}
            disabled={loadingTransactions}
            className="mt-4 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
          >
            {loadingTransactions ? 'Loading...' : 'Load More'}
          </button>
        )}
      </div>
    </div>
  );
};

export default AddressPage;
